import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import {
  UsersThree, UserPlus, UserMinus, ShieldCheck, Clock, ChartLineUp,
  Warning, Info, MagnifyingGlass, LinkSimple,
} from "@phosphor-icons/react";

/**
 * Admin → Petro-Pass Portal Health
 * --------------------------------
 * READ-ONLY. Backed by `GET /api/admin/portal-health`. This tile never
 * writes, sends, activates, or provisions. All action controls
 * (add user, invite, revoke, authorize lanes) live on their existing
 * admin pages — the row link opens the CustomerDetail page.
 *
 * Phase 3E-8 (Feb 27 2026).
 */
export default function PortalHealth() {
  const [filter, setFilter] = useState("ALL");
  const [sort, setSort] = useState("longest_awaiting");
  const [onlyPpUs, setOnlyPpUs] = useState(false);
  const [neverSignedIn, setNeverSignedIn] = useState(false);
  const [includeFixtures, setIncludeFixtures] = useState(false);
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    (async () => {
      try {
        const params = new URLSearchParams();
        if (filter !== "ALL") params.set("filter", filter);
        params.set("sort", sort);
        if (onlyPpUs) params.set("only_pp_us", "true");
        if (neverSignedIn) params.set("never_signed_in", "true");
        if (includeFixtures) params.set("include_fixtures", "true");
        const r = await api.get(`/admin/portal-health?${params.toString()}`);
        if (!alive) return;
        setData(r.data);
      } catch (ex) {
        if (!alive) return;
        setErr(ex?.response?.data?.detail || "Could not load portal health.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [filter, sort, onlyPpUs, neverSignedIn, includeFixtures]);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter(r =>
      (r.company_name || "").toLowerCase().includes(q) ||
      (r.customer_id || "").toLowerCase().includes(q));
  }, [data, query]);

  return (
    <div className="p-6 space-y-5" data-testid="portal-health-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
            Petro-Pass Portal Health
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Read-only operational view: who has a portal user, who is
            still waiting to sign in for the first time, and who is
            intentionally parked. This page never writes to any record
            and never sends anything to a customer.
          </p>
        </div>
      </header>

      {err && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex items-start gap-2"
             data-testid="portal-health-error">
          <Warning size={16} weight="bold" className="mt-[1px]" /> {err}
        </div>
      )}

      {loading || !data ? (
        <div className="text-slate-500 text-sm" data-testid="portal-health-loading">Loading…</div>
      ) : (
        <>
          {/* CUSTOMER FUNNEL — mutually exclusive per PP-US authorized customer */}
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="mb-2 flex items-baseline gap-3">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Customer funnel
              </div>
              <div className="text-[11px] text-slate-400">
                Real PP-US authorized customers · one bucket per customer ·
                ACTIVE + INVITED + NO_USER + DISABLED + REMOVED = {data.counts.real_customers_pp_us_authorized}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
                 data-testid="portal-health-summary">
              <SummaryCard icon={<ShieldCheck size={18} weight="bold" />}
                           tone="slate"
                           label="Real PP-US authorized"
                           value={data.counts.real_customers_pp_us_authorized}
                           testId="ph-real-authorized"
                           onClick={() => { setFilter("ALL"); setOnlyPpUs(true); }} />
              <SummaryCard icon={<UsersThree size={18} weight="bold" />}
                           tone="emerald"
                           label="Active customers"
                           value={data.counts.active_portal_users}
                           testId="ph-active"
                           onClick={() => { setFilter("ACTIVE"); setOnlyPpUs(true); }} />
              <SummaryCard icon={<UserPlus size={18} weight="bold" />}
                           tone="indigo"
                           label="Invited / awaiting sign-in"
                           value={data.counts.invited_awaiting_first_login}
                           testId="ph-invited"
                           onClick={() => { setFilter("INVITED"); setOnlyPpUs(true); }} />
              <SummaryCard icon={<Info size={18} weight="bold" />}
                           tone="amber"
                           label="No portal user"
                           value={data.counts.no_portal_user}
                           testId="ph-no-user"
                           onClick={() => { setFilter("NO_USER"); setOnlyPpUs(true); }} />
              <SummaryCard icon={<UserMinus size={18} weight="bold" />}
                           tone="rose"
                           label="Disabled + Removed"
                           value={data.counts.disabled_users + data.counts.removed_users}
                           testId="ph-inactive"
                           onClick={() => { setFilter("DISABLED"); setOnlyPpUs(true); }} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
              <SummaryCard icon={<Clock size={18} weight="bold" />}
                           tone="orange"
                           label="Never signed in"
                           value={data.counts.never_signed_in}
                           testId="ph-never"
                           onClick={() => { setNeverSignedIn(true); setOnlyPpUs(true); setFilter("ALL"); }} />
              <SummaryCard icon={<ChartLineUp size={18} weight="bold" />}
                           tone="emerald"
                           label="Activated (7d)"
                           value={data.counts.activated_last_7d}
                           testId="ph-activated-7d" />
              <SummaryCard icon={<ChartLineUp size={18} weight="bold" />}
                           tone="emerald"
                           label="Activated (30d)"
                           value={data.counts.activated_last_30d}
                           testId="ph-activated-30d" />
            </div>
          </div>

          {/* USER INVENTORY — row counts, NOT customer counts */}
          <div className="rounded-xl border border-slate-200 bg-white p-3"
               data-testid="portal-health-user-inventory">
            <div className="mb-2 flex items-baseline gap-3">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                User inventory
              </div>
              <div className="text-[11px] text-slate-400">
                customer_users rows on real customers · a customer with
                multiple users inflates these counts · separate from
                the funnel above
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              <InventoryCell label="Total rows"    value={data.user_inventory.total}    testId="ph-inv-total" />
              <InventoryCell label="Active rows"   value={data.user_inventory.active}   testId="ph-inv-active" />
              <InventoryCell label="Invited rows"  value={data.user_inventory.invited}  testId="ph-inv-invited" />
              <InventoryCell label="Disabled rows" value={data.user_inventory.disabled} testId="ph-inv-disabled" />
              <InventoryCell label="Removed rows"  value={data.user_inventory.removed}  testId="ph-inv-removed" />
            </div>
          </div>

          {/* controls */}
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3"
               data-testid="portal-health-controls">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">Filter</span>
              <select value={filter} onChange={e => setFilter(e.target.value)}
                      className="rounded-lg border border-slate-300 px-2 py-1.5"
                      data-testid="ph-filter">
                <option value="ALL">All</option>
                <option value="ACTIVE">Active</option>
                <option value="INVITED">Awaiting Activation</option>
                <option value="NO_USER">No Portal User</option>
                <option value="DISABLED">Disabled</option>
                <option value="REMOVED">Removed</option>
                <option value="PP_US">Petro-Pass USA Authorized</option>
                <option value="NEVER_SIGNED_IN">Never Signed In</option>
              </select>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">Sort</span>
              <select value={sort} onChange={e => setSort(e.target.value)}
                      className="rounded-lg border border-slate-300 px-2 py-1.5"
                      data-testid="ph-sort">
                <option value="longest_awaiting">Longest Awaiting Activation</option>
                <option value="recent_activation">Most Recent Activation</option>
                <option value="recent_sign_in">Most Recent Sign-In</option>
                <option value="company">Company Name</option>
              </select>
            </div>
            <label className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={onlyPpUs}
                     onChange={e => setOnlyPpUs(e.target.checked)}
                     data-testid="ph-only-ppus" />
              <span className="text-slate-700">PP-US only</span>
            </label>
            <label className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={neverSignedIn}
                     onChange={e => setNeverSignedIn(e.target.checked)}
                     data-testid="ph-never-signed-in" />
              <span className="text-slate-700">Never signed in</span>
            </label>
            <label className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={includeFixtures}
                     onChange={e => setIncludeFixtures(e.target.checked)}
                     data-testid="ph-include-fixtures" />
              <span className="text-slate-700">Show test fixtures</span>
            </label>
            <div className="ml-auto flex items-center gap-2 text-sm">
              <MagnifyingGlass size={14} className="text-slate-400" />
              <input type="text" value={query}
                     onChange={e => setQuery(e.target.value)}
                     placeholder="Search company / id"
                     className="rounded-lg border border-slate-300 px-2 py-1.5 w-64"
                     data-testid="ph-search" />
            </div>
          </div>

          {/* customer table */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 text-sm font-medium text-slate-900">
              Customers ({rows.length})
              <span className="ml-2 text-xs text-slate-400 font-normal">
                Generated {new Date(data.generated_at).toLocaleString()}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="portal-health-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2">Company</th>
                    <th className="text-left px-4 py-2">Portal Status</th>
                    <th className="text-left px-4 py-2">Role · Primary</th>
                    <th className="text-left px-4 py-2">PP-US</th>
                    <th className="text-left px-4 py-2">Last Sign-In</th>
                    <th className="text-left px-4 py-2">Activated</th>
                    <th className="text-left px-4 py-2">Days Awaiting</th>
                    <th className="text-left px-4 py-2">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-8 text-slate-500"
                            data-testid="portal-health-empty">
                      No customers match this filter.
                    </td></tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.customer_id} className="border-t border-slate-100"
                        data-testid={`ph-row-${r.customer_id}`}>
                      <td className="px-4 py-2 text-slate-800">
                        <Link to={`/customers/${r.customer_id}`}
                              className="hover:underline"
                              data-testid={`ph-link-${r.customer_id}`}>
                          {r.company_name || r.customer_id}
                        </Link>
                        <div className="text-xs text-slate-400 font-mono">
                          {r.customer_id}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <StatusBadge status={r.portal_status} />
                        {r.user_count > 1 && (
                          <span className="ml-2 text-xs text-slate-500">
                            ({r.user_count} users)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-700">
                        {r.primary_user_role || "—"}
                        {r.primary_user_is_primary && (
                          <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px]">
                            primary
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {r.petro_pass_us_authorized
                          ? <span className="text-emerald-700 font-medium">Authorized</span>
                          : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-2 text-xs font-mono text-slate-600">
                        {r.last_sign_in_at
                          ? new Date(r.last_sign_in_at).toLocaleString()
                          : <span className="text-slate-400">Never / N/A</span>}
                      </td>
                      <td className="px-4 py-2 text-xs font-mono text-slate-600">
                        {r.activation_used_at
                          ? new Date(r.activation_used_at).toLocaleDateString()
                          : <span className="text-slate-400">Not recorded</span>}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-700">
                        {r.portal_status === "INVITED" && r.days_since_invitation != null
                          ? `Invited ${r.days_since_invitation}d ago`
                          : "—"}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {r.portal_status_note && (
                          <span className="rounded-md bg-slate-100 border border-slate-200 px-2 py-0.5 text-slate-700">
                            {r.portal_status_note}
                          </span>
                        )}
                        {r.operator_deferral_reason && (
                          <div className="mt-1 text-[11px] text-slate-500 max-w-xs">
                            {r.operator_deferral_reason}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500 flex items-center gap-2">
              <LinkSimple size={12} />
              Row click opens the Customer detail page — all user
              management actions live on the existing screens. Emails
              are masked by policy; this page never triggers outbound
              communication.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    ACTIVE:   "bg-emerald-50 text-emerald-700 border-emerald-200",
    INVITED:  "bg-indigo-50 text-indigo-700 border-indigo-200",
    NO_USER:  "bg-slate-50 text-slate-700 border-slate-200",
    DISABLED: "bg-orange-50 text-orange-700 border-orange-200",
    REMOVED:  "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <span className={`inline-block rounded-md border px-2 py-0.5 text-xs font-medium ${map[status] || map.NO_USER}`}>
      {status.replace("_", " ")}
    </span>
  );
}

function InventoryCell({ label, value, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
         data-testid={testId}>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="text-lg font-semibold tabular-nums text-slate-800">{value}</div>
    </div>
  );
}

function SummaryCard({ icon, tone, label, value, onClick, testId }) {
  const toneMap = {
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  const Cmp = onClick ? "button" : "div";
  return (
    <Cmp
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-xl border p-4 hover:shadow-sm transition ${toneMap[tone] || toneMap.slate}`}
    >
      <div className="flex items-center justify-between">
        <div>{icon}</div>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
      </div>
      <div className="mt-2 text-xs font-medium">{label}</div>
    </Cmp>
  );
}
